import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { Box, Button, Skeleton, Theme, Typography } from "@outschool/backpack";
import {
  CategoryPage,
  ContentfulMagnetPage
} from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { magnetPageUrl, onlineClassesPath } from "@outschool/routes";
import {
  SimilarArticlesMagnetPageCarousel,
  SubjectIcon
} from "@outschool/ui-components-website";
import {
  Container,
  PageWidthSection
} from "@outschool/ui-legacy-component-library";
import { useDeviceTypeContext } from "@outschool/ui-utils";
import React from "react";
import { Link, useParams } from "react-router-dom";

import ContentfulPageLayout from "../../components/contentful/ContentfulPageLayout";
import NotFoundPage from "../NotFoundPage";
import BodyMagnetPage from "./BodyMagnetPage";
import { MAGNET_PAGE_PATHS, MAX_WIDTH, TOPICS } from "./constants";
import HeaderMagnetPage from "./HeaderMagnetPage";
import RelatedTopicsMagnetPage from "./RelatedTopicsMagnetPage";
import TagsList from "./TagsList";
import UpsellCarouselMagnetPage from "./UpsellCarousel";
import useMagnetPageContent from "./useMagnetPageContent";

export default function MagnetPage({
  path
}: {
  path: (typeof MAGNET_PAGE_PATHS)[keyof typeof MAGNET_PAGE_PATHS];
}) {
  // get the base path from the first part of the location pathname
  const basePath = "/" + path;
  const { slug, gradeNameSlug } = useParams<{
    slug: string;
    gradeNameSlug: string;
  }>();

  const { deviceType } = useDeviceTypeContext();

  const { t } = useTranslation("client\\routes\\MagnetPages");
  const { data, loading, error } = useMagnetPageContent({
    path
  });

  const magnetPage = data?.contentfulMagnetPageCollection?.items?.[0];

  if ((!loading && !magnetPage) || error) {
    return <NotFoundPage />;
  }

  const titleText = `${magnetPage?.title} | Outschool`;
  const plainTextSummary = documentToPlainTextString(
    magnetPage?.summary?.json
  ).replace(/\u00A0/g, " "); // replace &nbsp; with regular spaces

  const toLink = onlineClassesPath(magnetPage?.category.slug || "");

  const linkTrackingUniqueId = toLink.split("/")[1] || "/";
  const linkTrackingName = "online_classes_link";

  return (
    <ContentfulPageLayout
      deviceType={deviceType}
      title={titleText}
      metaTags={{
        description: plainTextSummary,
        image: magnetPage?.headerImage?.url || "",
        canonicalUrl: magnetPageUrl(
          magnetPage?.magnetPageSlug || slug,
          basePath,
          gradeNameSlug
        )
      }}
      trackingName="magnetPageImpression"
    >
      <Container sx={{ maxWidth: "692px" }}>
        {!loading && magnetPage ? (
          <>
            <Link
              {...{
                to: toLink,
                trackingName: linkTrackingName,
                trackingUniqueId: linkTrackingUniqueId
              }}
            >
              <SubjectIcon
                subjectName={magnetPage?.category.nameTitleCased || ""}
              />
              {magnetPage?.category.nameTitleCased || ""}
            </Link>
            <HeaderMagnetPage magnetPage={magnetPage as ContentfulMagnetPage} />
            <BodyMagnetPage magnetPage={magnetPage as ContentfulMagnetPage} />
          </>
        ) : (
          <Skeleton variant="rectangular" width={MAX_WIDTH} height="100vh" />
        )}
      </Container>

      {magnetPage?.category && (
        <Box
          sx={{
            py: "40px"
          }}
        >
          <RelatedTopicsMagnetPage
            category={magnetPage?.category as CategoryPage}
          />
        </Box>
      )}

      <PageWidthSection sx={{ backgroundColor: "yellow2" }}>
        <Box
          sx={{
            justifyContent: "center",
            textAlign: "center"
          }}
        >
          <Typography
            variant="h3"
            sx={(theme: Theme) => ({
              color: "primary.900",
              fontSize: `${theme.typography.pxToRem(36)} !important`
            })}
          >
            {t("Explore 140,000+ classes led by qualified teachers")}
          </Typography>
          <Link to={onlineClassesPath()}>
            <Button
              variant="contained"
              sx={{
                marginTop: "10px"
              }}
            >
              {t("Browse classes")}
            </Button>
          </Link>
        </Box>
      </PageWidthSection>

      {magnetPage?.category && magnetPage?.relatedMagnetPages?.items && (
        <SimilarArticlesMagnetPageCarousel
          category={magnetPage?.category as CategoryPage}
          articles={
            magnetPage?.relatedMagnetPages?.items as ContentfulMagnetPage[]
          }
          basePath={basePath}
        />
      )}

      {magnetPage?.upsellClassCarousel?.listings?.listings && (
        <UpsellCarouselMagnetPage
          listings={magnetPage?.upsellClassCarousel?.listings?.listings}
          title={
            magnetPage?.upsellClassCarousel?.title ||
            `${magnetPage?.category?.nameTitleCased} classes` ||
            ""
          }
        />
      )}

      <TagsList
        title={t("Topics you may be interested in")}
        items={TOPICS.map(el => ({
          label: el,
          url: onlineClassesPath(el)
        }))}
      />
    </ContentfulPageLayout>
  );
}
